<template>
  <div
    :id="`${source}_audit_trails`"
    :class="`flex flex-col w-full gap-2 proxima_nova ${padding}`"
  >
    <SkeletonLoader
      v-if="isLoading && !audits?.length"
      :page="loaderPages.AUDIT_TRAIL"
    />

    <div
      v-else-if="filteredAudits?.length > 0"
      class="flex flex-col gap-2 w-full overflow-y-scroll"
      :style="`height: ${height}`"
      @scroll="handleScroll"
      ref="scrollContainer"
    >
      <AuditTrailRenderer
        :source="source"
        :audits="filteredAudits"
        :sequence-name="entity?.name"
        :is-last-page="!paginationData.hasMore"
      />
    </div>

    <EmptyScreen
      v-else-if="!isLoading"
      :source="`${source}_audit_trails`"
      text="No audits found!"
    />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, nextTick, computed, watch } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import {
  allowedActionBySources,
  backgroundJobAllowedCombinations,
  auditActionBySource,
} from "@/src/constants/auditConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import AuditTrailRenderer from "@/src/components/audit_components/AuditTrailRenderer.vue";

const props = defineProps({
  source: { type: String, required: true },
  entity: { type: Object, required: true },
  height: { type: String, default: "82vh" },
  padding: { type: String, default: "py-2 px-5" },
  visible: { type: Boolean, default: true },
});

const audits = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const scrollContainer = ref(null);

const paginationData = ref({
  currentPage: 1,
  pageSize: 140,
  hasMore: true,
  total: 0,
  entityId: null,
  entityType: null,
});

const filteredAudits = computed(() => {
  return audits.value.filter((audit) => {
    if (allowedActionBySources.includes(audit.action_by_source)) {
      return true;
    }

    if (audit.action_by_source === auditActionBySource.BACKGROUND_JOB) {
      return backgroundJobAllowedCombinations.some(
        (combination) =>
          combination.entityType === audit.entity_type &&
          combination.operationType === audit.operation_type,
      );
    }

    return false;
  });
});

const resetPagination = () => {
  try {
    paginationData.value = {
      currentPage: 1,
      pageSize: 140,
      hasMore: true,
      total: 0,
      entityId: null,
      entityType: null,
    };

    audits.value = [];
  } catch (error) {
    console.error("An error occurred while resetting pagination: ", error);
  }
};

const loadMoreAudits = async () => {
  try {
    if (isLoadingMore.value || !paginationData.value.hasMore) return;

    const nextPage = paginationData.value.currentPage + 1;
    await toFetchAuditLogs(nextPage, true);
  } catch (error) {
    console.error("An error occurred while loading more audits: ", error);
  }
};

const handleScroll = async (event) => {
  try {
    const { scrollTop, scrollHeight, clientHeight } = event.target;
    const threshold = 100;

    if (scrollHeight - (scrollTop + clientHeight) <= threshold) {
      if (paginationData.value.hasMore && !isLoadingMore.value) {
        await loadMoreAudits();
      }
    }
  } catch (error) {
    console.error("An error occurred while handling scroll: ", error);
  }
};

const toFetchAuditLogs = async (page = 1, isLoadMore = false) => {
  try {
    if (!isLoadMore) {
      isLoading.value = true;
    } else {
      isLoadingMore.value = true;
    }

    const params = {
      page: page,
      size: paginationData.value.pageSize,
      entity_id: props.entity?.entity_id,
      entity_type: props.entity?.entity_type,
    };

    const response = await DashboardService.fetchAuditLogs(params);

    if (response?.success) {
      const newAudits = response?.audit_logs || [];

      if (isLoadMore) {
        const previousScrollHeight = scrollContainer.value?.scrollHeight || 0;
        audits.value = [...audits.value, ...newAudits];

        await nextTick();

        if (scrollContainer.value) {
          const newScrollHeight = scrollContainer.value.scrollHeight;
          const heightDifference = newScrollHeight - previousScrollHeight;
          scrollContainer.value.scrollTop =
            scrollContainer.value.scrollTop + heightDifference - 50;
        }
      } else {
        audits.value = newAudits;
      }

      paginationData.value = {
        currentPage: response.page || page,
        pageSize: response.size || paginationData.value.pageSize,
        hasMore: response.hasMore || false,
        total: response.total || 0,
        entityId: response.entity_id || props.entity?.entity_id,
        entityType: response.entity_type || props.entity?.entity_type,
      };
    }
  } catch (error) {
    console.error(
      "An error occurred while trying to fetch audit logs: ",
      error,
    );
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

let delayedFetchTimer = null;

watch(
  () => props.visible,
  (isVisible) => {
    clearTimeout(delayedFetchTimer);
    if (isVisible) {
      resetPagination();
      toFetchAuditLogs();
      delayedFetchTimer = setTimeout(() => {
        toFetchAuditLogs();
      }, 3000);
    }
  },
);

onMounted(() => {
  if (!props.visible) return;
  resetPagination();
  toFetchAuditLogs();
  delayedFetchTimer = setTimeout(() => {
    toFetchAuditLogs();
  }, 3000);
});

onUnmounted(() => {
  clearTimeout(delayedFetchTimer);
});
</script>
